/**
 * MFA Verification Component
 * 
 * Used during login when MFA is required.
 * Supports TOTP, Email OTP, and WhatsApp OTP.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { 
  Shield, 
  Smartphone,
  Mail, 
  MessageSquare, 
  RefreshCw,
  ArrowLeft,
  CheckCircle
} from 'lucide-react';
import api from '../lib/api';

const MFAVerification = ({ 
  mfaSessionToken, 
  availableMethods = ['totp', 'email'],
  userEmail,
  onSuccess,
  onCancel,
}) => {
  const [selectedMethod, setSelectedMethod] = useState(availableMethods[0] || 'email');
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [trustDevice, setTrustDevice] = useState(true);
  const [countdown, setCountdown] = useState(0);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Send OTP code
  const handleSendCode = async () => {
    try {
      setSendingCode(true);
      const response = await api.post('/auth/mfa/send-code', null, {
        params: {
          mfa_session_token: mfaSessionToken,
          method: selectedMethod,
        },
      });
      
      if (response.data.success) {
        setCodeSent(true);
        setCountdown(60); // 60 second cooldown
        toast.success(`Code sent to ${response.data.sent_to}`);
      }
    } catch (error) {
      console.error('Failed to send code:', error);
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  };

  // Verify MFA code
  const handleVerify = async () => {
    if (!code || code.length < 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    try {
      setVerifying(true);
      const response = await api.post('/auth/mfa/verify', {
        mfa_session_token: mfaSessionToken,
        code: code,
        method: selectedMethod,
        trust_device: trustDevice,
      });
      
      if (response.data.access_token) {
        toast.success('Verification successful!');
        onSuccess(response.data);
      } else {
        toast.error('Verification failed');
      }
    } catch (error) {
      console.error('MFA verification failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid verification code');
      setCode('');
    } finally {
      setVerifying(false);
    }
  };

  // Method icons
  const methodIcons = {
    totp: <Smartphone className="h-5 w-5" />,
    email: <Mail className="h-5 w-5" />,
    whatsapp: <MessageSquare className="h-5 w-5 text-green-600" />,
  };

  const methodNames = {
    totp: 'Authenticator App',
    email: 'Email',
    whatsapp: 'WhatsApp',
  };

  return (
    <Card className="w-full max-w-md mx-auto" data-testid="mfa-verification">
      <CardHeader className="text-center">
        <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
          <Shield className="h-6 w-6 text-primary" />
        </div>
        <CardTitle>Two-Factor Authentication</CardTitle>
        <CardDescription>
          Enter the verification code to continue
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Method Selection (if multiple available) */}
        {availableMethods.length > 1 && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Verification Method</label>
            <div className="grid grid-cols-3 gap-2">
              {availableMethods.map((method) => (
                <Button
                  key={method}
                  variant={selectedMethod === method ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setSelectedMethod(method);
                    setCode('');
                    setCodeSent(false);
                  }}
                  className="flex items-center gap-2"
                >
                  {methodIcons[method]}
                  <span className="hidden sm:inline">{methodNames[method]}</span>
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* TOTP Input */}
        {selectedMethod === 'totp' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Enter the 6-digit code from your authenticator app
            </p>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="text-center text-2xl tracking-widest font-mono"
              maxLength={6}
              autoFocus
              data-testid="totp-input"
            />
          </div>
        )}

        {/* Email/WhatsApp OTP */}
        {(selectedMethod === 'email' || selectedMethod === 'whatsapp') && (
          <div className="space-y-4">
            {!codeSent ? (
              <div className="text-center space-y-4">
                <p className="text-sm text-muted-foreground">
                  We&apos;ll send a verification code to your {selectedMethod === 'email' ? 'email' : 'WhatsApp'}
                </p>
                <Button
                  onClick={handleSendCode}
                  disabled={sendingCode}
                  className="w-full"
                  data-testid="send-code-btn"
                >
                  {sendingCode ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      {methodIcons[selectedMethod]}
                      <span className="ml-2">Send Code</span>
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-2 text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-sm">Code sent!</span>
                </div>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest font-mono"
                  maxLength={6}
                  autoFocus
                  data-testid="otp-input"
                />
                <div className="text-center">
                  <Button
                    variant="link"
                    size="sm"
                    onClick={handleSendCode}
                    disabled={countdown > 0 || sendingCode}
                  >
                    {countdown > 0 
                      ? `Resend code in ${countdown}s` 
                      : 'Resend code'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Trust Device Checkbox */}
        {(selectedMethod === 'totp' || codeSent) && (
          <label className="flex items-center gap-3 p-3 bg-muted rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={trustDevice}
              onChange={(e) => setTrustDevice(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <div>
              <p className="text-sm font-medium">Trust this device for 60 days</p>
              <p className="text-xs text-muted-foreground">
                You won&apos;t need to verify again on this device
              </p>
            </div>
          </label>
        )}

        {/* Verify Button */}
        {(selectedMethod === 'totp' || codeSent) && (
          <Button
            onClick={handleVerify}
            disabled={verifying || code.length < 6}
            className="w-full"
            data-testid="verify-btn"
          >
            {verifying ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify & Sign In'
            )}
          </Button>
        )}

        {/* Cancel Button */}
        <Button
          variant="ghost"
          onClick={onCancel}
          className="w-full"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Login
        </Button>
      </CardContent>
    </Card>
  );
};

export default MFAVerification;
